import React, {
	forwardRef,
	MutableRefObject,
	KeyboardEventHandler,
	ReactElement,
} from 'react';
import './InputCheckbox.css';
import Label from '../Label';
import { IToolTipProps } from '../ToolTip/ToolTip';
import plus from '../../assets/icons/InputCheckbox/Plus.svg';
import check from '../../assets/icons/InputCheckbox/check.svg';
import CardContainer from '../CardContainer';
import Row from '../../foundational/Layout/Row';
import Icon from '../Icon';
import correctIcon from '../../assets/icons/icon-correct.svg';
import incorrectIcon from '../../assets/icons/icon-incorrect.svg';
export interface IInputCheckboxProps {
	className?: string;
	labelClass?: string;
	textClass?: string;
	value: boolean;
	setValue: THandlerFunction<boolean>;
	label?: string | ReactElement | ReactElement[];
	name?: string;
	id?: string;
	disabled?: boolean;
	tabIndex?: number;
	required?: boolean;
	helperText?: string | ReactElement | ReactElement[];
	tooltip?: string;
	description?: string | ReactElement | ReactElement[];
	error?: boolean;
	errorText?: string;
	showErrorAndDescription?: boolean;
	onChange?: THandlerFunction<any>;
	tooltipNotchPosition?: IToolTipProps['notchPosition'];
	mobileTooltipNotchPosition?: IToolTipProps['notchPosition'];
	variant?: 'checkbox' | 'bubble' | 'quiz';
	status?: undefined | 'correct' | 'incorrect';
	iconPath?: string;
	checkboxId?: string;
	ariaLabel?: string;
	circularCheckbox?:boolean;
}

const InputCheckbox = forwardRef(
	(
		{
			className = '',
			labelClass = '',
			textClass = '',
			value = false,
			setValue = () => { },
			label,
			name = '',
			id = '',
			disabled = false,
			tabIndex = 0,
			required = false,
			circularCheckbox=false,
			helperText = '',
			tooltip = '',
			description = '',
			error = false,
			errorText = '',
			showErrorAndDescription = false,
			onChange = () => { },
			tooltipNotchPosition = 'bottom-left',
			mobileTooltipNotchPosition = 'bottom-right',
			variant = 'checkbox',
			status,
			iconPath,
			checkboxId = '',
			ariaLabel = undefined,
			...rest
		}: IInputCheckboxProps,
		ref: any,
	) => {
		const handleOnClickForARIA = () => {
			setValue(!value);
		};
		const handleSpaceKeyDownForARIA: KeyboardEventHandler<HTMLDivElement> = (
			e,
		) => {
			if (e.key === ' ') {
				e.preventDefault();
				setValue(!value);
			}
		};
		const checkboxVariant = () => {
			return (
				<>
					<label
						htmlFor={id}
						className={`InputCheckbox flex ${className} ${disabled ? 'disabled' : ''
							}`}
					>
						<input
							id={id}
							ref={ref}
							type='checkbox'
							className='InputCheckbox__hidden-input'
							name={name}
							checked={value}
							aria-required={required}
							disabled={disabled}
							onChange={onChange}
							{...rest}
						/>
						<div
							className='InputCheckbox__alignment-container'
							onClick={handleOnClickForARIA}
							onKeyDown={handleSpaceKeyDownForARIA}
						>
							<span
								className={`InputCheckbox__checkbox ${error ? 'InputCheckbox__checkbox__error' : ''
									}`}
								aria-checked={value}
								role='checkbox'
								id={checkboxId}
								tabIndex={0}
								aria-disabled={disabled}
								aria-label={ariaLabel}
							>
								<span className='InputCheckbox__checkbox__mark' />
							</span>
							<Label
								helperText={helperText}
								className={labelClass}
								textClass={textClass}
								tooltip={tooltip}
								error={error}
								tooltipAlign={tooltipNotchPosition}
								mobileTooltipAlign={mobileTooltipNotchPosition}
								required={required}
							>
								{label}
							</Label>
						</div>
					</label>
					{showErrorAndDescription ? (
						<>
							{error && errorText && !disabled && (
								<p className='InputCheckbox__required_error'>
									{errorText}
								</p>
							)}
							{description && (
								<p className='InputCheckbox__description'>
									{description}
								</p>
							)}
						</>
					) : (
						((error && errorText && !disabled) || description) && (
							<p
								className={`InputCheckbox${error && errorText && !disabled
									? '__required_error'
									: '__description'
									}`}
							>
								{error && errorText && !disabled
									? errorText
									: description}
							</p>
						)
					)}
				</>
			);
		};

		const bubbleVariant = () => {
			return (
				<>
					<label
						htmlFor={id}
						className={`InputCheckbox ${className}`}
					>
						<input
							id={id}
							ref={ref}
							type='checkbox'
							className='InputCheckbox__hidden-input'
							name={name}
							checked={value}
							onChange={onChange}
							{...rest}
						/>
						<div
							className={`InputCheckbox__alignment-container--bubble ${value ? 'InputCheckbox__bubbleMarked' : ''} `}
							onClick={handleOnClickForARIA}
							onKeyDown={handleSpaceKeyDownForARIA}
							tabIndex={0}
							aria-label={label as string}
							aria-checked={value}
							role='checkbox'
						>
							<Label
								helperText={helperText}
								className={`InputCheckbox__bubbleText ${labelClass}`}
								textClass={textClass}
								tooltip={tooltip}
								tooltipAlign={tooltipNotchPosition}
								mobileTooltipAlign={mobileTooltipNotchPosition}
							>
								{label}
							</Label>
							<span
								className={`InputCheckbox__checkImage`}
								id={checkboxId}
								aria-label={value ? 'check mark' : 'plus sign'}
							>
								{value ? <img src={check} /> : <img src={plus} />}
							</span>
						</div>
					</label>
					{description && (
						<p className='InputCheckbox__description'>
							{description}
						</p>
					)}
				</>
			);
		}

		const quizVariant = () => {
			const finalStatus = status;
			return (
				<>
					<CardContainer
						className={`InputCheckbox mb-4 InputCheckbox__quiz-container ${className} ${finalStatus ? (finalStatus === 'correct' ? 'InputCheckbox__correct' : 'InputCheckbox__incorrect') : ''}`}
						borderSettings={value ? { borderColor: 'blue-creditone' } : { borderColor: 'grey-chalice-50' }}
						onClick={() => !disabled && setValue(!value)}
						onKeyDown={(e) => {
							if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
								e.preventDefault();
								setValue(!value);
							}
						}}
						tabIndex={disabled ? -1 : 0}
						role="checkbox"
						aria-checked={value}>
						<Row justify='start'>
							<label
								htmlFor={id}
								className={`InputCheckbox ${className} ${disabled? '': ''}`}
								style={{ width: '100%', cursor: 'pointer' }}>
								<input
									id={id}
									ref={ref}
									type='checkbox'
									className='InputCheckbox__hidden-input'
									name={name}
									checked={value}
									aria-required={required}
									disabled={disabled}
									aria-disabled={disabled}
									onChange={onChange}
									{...rest}
								/>
								<div className='InputCheckbox__alignment-container' >
									<span
										className={`InputCheckbox__checkbox ${circularCheckbox ? 'InputCheckbox__checkbox__rounded' :''} ${error && !disabled
											? 'InputCheckbox__checkbox__error'
											: ''} 
											${finalStatus
												? `InputCheckbox__checkbox__status`
												: ''}`}
										id={checkboxId}
									>
										{finalStatus && (finalStatus === 'correct' ? (
											<Icon customSize={24} src={correctIcon} alt="Correct" />
										) : (
											<Icon customSize={20} src={incorrectIcon} alt="Incorrect" />
										)
										)}
										{!finalStatus && <span className='InputCheckbox__checkbox__mark' />}
									</span>
									{iconPath &&
										<Icon size='utility' src={iconPath} />}
									<Label
										helperText={helperText}
										className={labelClass}
										textClass={textClass}
										tooltip={tooltip}
										error={error}
										tooltipAlign={tooltipNotchPosition}
										mobileTooltipAlign={mobileTooltipNotchPosition}
										required={required}>
										{label}
									</Label>
								</div>
							</label>
							{showErrorAndDescription ? (
								<>
									{error && errorText && !disabled && (
										<p className='InputCheckbox__required_error'>
											{errorText}
										</p>
									)}
									{description && (
										<p className='InputCheckbox__description'>
											{description}
										</p>
									)}
								</>
							) : (
								((error && errorText !== '' && !disabled) ||
									description) && (
									<p
										className={`InputCheckbox${error && errorText && !disabled
											? '__required_error'
											: '__description'
											}`}
									>
										{error && errorText !== '' && !disabled
											? errorText
											: description}
									</p>
								)
							)}
						</Row>
					</CardContainer>
				</>
			);
		};

		const getButtonVariant = () => {
			switch (variant) {
				case 'bubble':
					return bubbleVariant();
				case 'quiz':
					return quizVariant();
				default:
					return checkboxVariant();
			}
		};

		return (
			<>
				{getButtonVariant()}
			</>
		);
	},
);

InputCheckbox.displayName = 'InputCheckbox';

export default InputCheckbox;
